import type { ServiceItemListQuery, Page } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { SearchX, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import type { ServiceItemView } from '../service-items.api';

interface ServiceItemsTableProps {
  page: Page<ServiceItemView>;
  columns: TableColumnsType<ServiceItemView>;
  list: ListQueryControls<ServiceItemListQuery>;
  isRefreshing: boolean;
  /** The "New service item" button, repeated inside the empty state when the catalogue has nothing yet. */
  newButton: ReactNode;
}

/** Search box plus the server-paged table; the old rows stay up, dimmed, while the next page loads. */
export function ServiceItemsTable({ page, columns, list, isRefreshing, newButton }: ServiceItemsTableProps) {
  if (page.total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={Wrench}
        title="No service items yet"
        description="Add what a truck is serviced for — an oil change, a brake check — with how often it falls due."
      >
        {newButton}
      </EmptyState>
    );
  }

  return (
    <>
      <ListSearch value={list.query.q} placeholder="Search code or name" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <Table
        rowKey="id"
        dataSource={page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={
          list.isFiltered
            ? { emptyText: <EmptyState icon={SearchX} title="No matching service items" description="No service items match this search." /> }
            : undefined
        }
        {...list.tableProps<ServiceItemView>(page.total)}
      />
    </>
  );
}
